// src/components/vault/VaultOnboarding.jsx  (UPDATED: inline loader on Save & Continue)
import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Stack, TextField, Button, Typography, CircularProgress } from "@mui/material";
import { uploadDocs } from "../../api/vaultApi";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

const STEPS = [
    {
        key: "legal",
        title: "Add a legal or identity document",
        subtitle: "Examples: Driver's license, passport, or CV"
    },
    {
        key: "education",
        title: "Add an education or board certification document",
        subtitle: "Examples: Medical school diploma or residency/fellowship certificate"
    },
    {
        key: "licenses",
        title: "Add a medical license document",
        subtitle: "Examples: State medical license or DEA registration"
    },
    {
        key: "resuscitation",
        title: "Add a resuscitation document",
        subtitle: "Examples: ACLS, ATLS, or PALS"
    },
    {
        key: "health",
        title: "Add a health or immunization document",
        subtitle: "Examples: Immunization records, influenza vaccine, or TB testing"
    },
];

export default function VaultOnboarding({ open, onClose, onUploaded }) {
    const [phase, setPhase] = React.useState("welcome");
    const [stepIdx, setStepIdx] = React.useState(0);
    const [file, setFile] = React.useState(null);
    const [name, setName] = React.useState("");
    const [expires, setExpires] = React.useState("");
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState("");

    React.useEffect(() => {
        if (open) {
            setPhase("welcome"); setStepIdx(0);
            setFile(null); setName(""); setExpires(""); setSaving(false); setError("");
        }
    }, [open]);

    const current = STEPS[stepIdx];

    const handleSkip = () => {
        if (stepIdx < STEPS.length - 1) {
            setStepIdx((i) => i + 1);
            setFile(null); setName(""); setExpires(""); setError("");
        } else {
            onClose?.();
        }
    };

    const handleNext = async () => {
        if (!file) return;
        setSaving(true);
        try {
            await uploadDocs({
                files: [file],
                slotKey: current.key,
                displayName: name.trim(),
                expiresAt: expires || null,
                makeCurrent: true
            });
            await onUploaded?.();
            setSaving(false);
            handleSkip(); // move to next / finish
        } catch (e) {
            setSaving(false);
            throw e;
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
            {phase === "welcome" ? (
                <>
                    <DialogTitle>Welcome to The Vault</DialogTitle>
                    <DialogContent dividers>
                        <Typography variant="body2" color="text.secondary">
                            A secure place to store and send your important documents.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="contained" onClick={() => setPhase("step")}>Next →</Button>
                    </DialogActions>
                </>
            ) : (
                <>
                    <DialogTitle>{current.title}</DialogTitle>
                    <DialogContent dividers>
                        <Stack spacing={1.5}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {current.subtitle}
                            </Typography>
                            <TextField label="Document Name" size="small" value={name} onChange={(e) => setName(e.target.value)} disabled={saving} required />
                            <Button variant="outlined" component="label" disabled={saving}>
                                {file ? "Change File" : "Choose File"}
                                <input
                                    type="file"
                                    hidden
                                    onChange={(e) => {
                                        const selectedFile = e.target.files?.[0];
                                        if (selectedFile) {
                                            if (selectedFile.size > MAX_FILE_SIZE) {
                                                setError("File is too large. Maximum file size is 5MB.");
                                                setFile(null);
                                            } else {
                                                setError("");
                                                setFile(selectedFile);
                                            }
                                        } else {
                                            setFile(null);
                                        }
                                        e.target.value = "";
                                    }}
                                    accept=".pdf,.png,.jpg,.jpeg"
                                />
                            </Button>
                            {file && <Typography variant="body2" color="textSecondary">{file.name}</Typography>}
                            {error && <Typography variant="body2" color="error">{error}</Typography>}
                            <TextField
                                size="small" type="date" label="Expiration date (optional)" InputLabelProps={{ shrink: true }}
                                value={expires} onChange={(e) => setExpires(e.target.value)} disabled={saving}
                            />
                            <Typography variant="caption" color="text.secondary">Step {stepIdx + 1} of {STEPS.length}</Typography>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleSkip} disabled={saving}>Skip</Button>
                        <Button variant="contained" onClick={handleNext} disabled={!file || !name.trim() || saving}>
                            {saving ? <CircularProgress size={18} sx={{ color: "white" }} /> : "Save & Continue"}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
}